/** The Invoice Manager module inside the user's `modules` array from the auth service. */
export const INVOICE_MANAGER_MODULE_GUID = "790af7b9-d9f8-4862-abf7-c0afeef174e8";

/**
 * Every permission guid the auth service defines for the Invoice Manager module.
 *
 * This list used to carry nine of them, and the gap was not harmless: an action
 * whose guid is absent here cannot be checked at all, so it runs ungated. The
 * New invoice button sat behind `UPDATE_INVOICE` for exactly that reason —
 * `CREATE_INVOICE` was missing from this file, and its absence was read as the
 * permission not existing rather than as this list being incomplete.
 *
 * Keep it complete. A name with no caller is cheap; a missing name is a hole.
 */
export const PERMISSIONS = {
  /* Queues and reads */
  MY_INVOICES: "339add39-b3f9-4089-8f47-b5d84996f536",
  ALL_INVOICES: "143bd534-f4a3-4825-9f65-dbfb8360ddbb",
  APPROVAL_REQUESTS: "ff8426ea-3130-4732-bd7c-be28ae759cd3",
  INVOICE_DETAILS: "e0c042c2-7960-4258-8bf5-741e263c6050",
  GET_AUDITS: "c8cf7ad7-1f16-4292-b4f1-850cde2e9840",
  INVOICE_CATEGORIES: "c14dd067-0357-486c-b382-51589d95107c",
  SHOW_INVOICE_CREATION_LOGS: "a8ed61b2-b35a-45b1-8500-b11f69c60741",

  /* Invoice lifecycle */
  CREATE_INVOICE: "4a9add39-b3f9-4089-8f47-b5d84996f96e",
  UPDATE_INVOICE: "87eb3fc4-cfec-4ba5-8e83-687bd0d03397",
  DELETE_INVOICE: "e6a8b0fd-7c41-461e-8db1-d60e5cd7c230",
  EXTRACT_INVOICE_DATA: "87eb3fc4-cfec-4ba5-3283-687bd0d04488",

  /* Review and approval */
  APPROVE_INVOICE: "0abcef6a-00c6-4167-a576-69be079c6e6e",
  REJECT_INVOICE: "4deb7d56-2af8-40b5-88e1-8493fd9a9209",
  COMPLETE_MANUAL_REVIEW: "328e01a6-2be9-4708-9f04-fe3f07293332",
  START_AUTOMATIC_REVIEW: "9f1a178e-0f92-4379-bbe7-e3da77ecda8b",

  /* Approval rules */
  APPROVAL_RULES: "9bea8b16-07fa-4f59-898a-712af7e742d4",
  CREATE_APPROVAL_RULE: "e8a76efd-828e-423f-847f-e3b857a991e6",
  UPDATE_APPROVAL_RULE: "14fbd34a-0c23-4627-bda2-216add004782",
  DELETE_APPROVAL_RULE: "a620dae6-afd7-4496-ae8d-e51a64538718",
  RUN_APPROVAL_RULES: "a52ee62f-6948-4ecc-8769-20fecd16d69e",
  APPROVAL_RULE_FIELDS: "c84d90b0-ea0d-45a9-8585-7d1ee54e1379",

  /* Attachments */
  ADD_ATTACHMENT: "7d502de5-2184-4f5c-a7ac-9920229d111a",
  DOWNLOAD_ATTACHMENT: "3c3a779e-306b-4fee-a8da-50301cf64cc7",
  DELETE_ATTACHMENT: "7bda990d-caa1-48cb-b221-1afa3f50589c",
};

export function extractPermissions(user) {
  return (
    user?.modules?.find((module) => module?.guid === INVOICE_MANAGER_MODULE_GUID)
      ?.permissions ?? []
  );
}

export function hasPermission(permissions, requiredGuid) {
  if (!requiredGuid) return true;
  return Boolean(permissions?.some((permission) => permission?.guid === requiredGuid));
}

export const NO_PERMISSION_PATH = "/no-permission";

/**
 * Landing routes in priority order. The old app spelled this list out twice —
 * once in `getIndexPath` and again in `getFirstRoutesUserHasPermission` — and
 * the two had already drifted apart. One list, two callers.
 */
const LANDING_ROUTES = [
  { path: "/my-invoices", permission: PERMISSIONS.MY_INVOICES },
  { path: "/invoice-manager", permission: PERMISSIONS.ALL_INVOICES },
  { path: "/approval-requests", permission: PERMISSIONS.APPROVAL_REQUESTS },
  { path: "/approval-rules", permission: PERMISSIONS.APPROVAL_RULES },
];

/** The first route this user is allowed to see, or the no-permission page. */
export function getLandingPath(permissions) {
  const match = LANDING_ROUTES.find((route) => hasPermission(permissions, route.permission));
  return match?.path ?? NO_PERMISSION_PATH;
}

/**
 * Where to send the user after login: back where they came from when that is
 * still reachable, otherwise their landing route.
 */
export function resolveRedirectPath(permissions, requestedPath) {
  const landing = getLandingPath(permissions);
  if (landing === NO_PERMISSION_PATH) return NO_PERMISSION_PATH;
  if (!requestedPath) return landing;

  const guarded = LANDING_ROUTES.find((route) => requestedPath.startsWith(route.path));
  if (!guarded) return requestedPath;

  return hasPermission(permissions, guarded.permission) ? requestedPath : landing;
}
